import React, { useCallback, useEffect, useRef, useState } from "react";
import { Alert, Button, Container, Dialog, DialogActions, DialogTitle, FormControl, InputLabel, MenuItem, Select, SelectChangeEvent, Slider, TextField } from "@mui/material";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { NavLink, useNavigate } from "react-router-dom";
import { Result, useZxing } from "react-zxing";
import { useMediaDevices } from "react-media-devices";
import { usePinch } from '@use-gesture/react'
import { Buffer } from "buffer";
import dayjs, { Dayjs } from "dayjs";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import utc from 'dayjs/plugin/utc'
import timezone from 'dayjs/plugin/timezone'

import { IGeneratedDocument } from "../../../types/document";
import { IMaterialInventory, IMaterialInventoryExportRequest } from "../../../types/material";
import { ISelectOption } from "../../../types/list";

import { AppConfig } from "../../../config/appConfig";
import { useUserContext } from "../../../contexts/userContext";
import { ErrorProvider } from "../../../classes/errorProvider";
import { AppSettingService } from "../../../services/appSettingService";

import { LoadService } from "../../../services/loadService";
import { UtilityService } from "../../../services/utilityService";

import Navigation from "../../../components/header";
import PermissionGate from "../../permissionGate";
import Spinner from "../../spinner";
import MaterialInventoryTable from "../../tables/materialInventoryTable";
import { Icons } from "../../icons";
import WorkingModal, { IWorkingModalProps } from '../../workingModal';
import scanBeep from '../../../assets/beep.mp3';

import "../../../styles/entity/materialInventoryList.scss";

dayjs.extend(utc)
dayjs.extend(timezone)

const MaterialInventoryList = () => {
	const userContext = useUserContext();
	const navigate = useNavigate();
	const [gridView, setGridView] = useState<IMaterialInventory[]>();
	const [filteredResults, setFilteredResults] = useState<IMaterialInventory[]>();

	//Filtering
	const [customerOptions, setCustomerOptions] = useState<ISelectOption[]>([]);
	const [loadStatusOptions, setLoadStatusOptions] = useState<ISelectOption[]>([{ name: "Loaded", value: "Loaded" }, { name: "Not Loaded", value: "Not Loaded" }]);
	const [containerOptions, setContainerOptions] = useState<ISelectOption[]>([]);
	const [idFilter, setIdFilter] = useState<string>("");
	const [customerFilter, setCustomerFilter] = useState<string>("All");
	const [loadStatusFilter, setLoadStatusFilter] = useState<string>("Not Loaded");
	const [containerFilter, setContainerFilter] = useState<string>("All");
	const [ageFilter, setAgeFilter] = useState<string>("custom");
	const [includedCustomerIds, setIncludedCustomerIds] = useState<string[]>([]);
	const [includedContainerIds, setIncludedContainerIds] = useState<string[]>([]);
	const [startDate, setStartDate] = useState<Dayjs | undefined>(undefined);
	const [endDate, setEndDate] = useState<Dayjs | undefined>(undefined);

	const [isLoadingMaterialInventories, setIsLoadingMaterialInventories] = useState(false);
	const [errorMessage, setErrorMessage] = useState<string | undefined>();
	const [workingModalParams, setWorkingModalParams] = useState<IWorkingModalProps | undefined>(undefined);

	const [showScanDialog, setShowScanDialog] = useState(false);
	const [videoDevices, setVideoDevices] = useState<MediaDeviceInfo[]>([]);
	const [selectedVideoDeviceId, setSelectedVideoDeviceId] = useState<string | undefined>(undefined);
	const [isVideoPlaying, setIsVideoPlaying] = useState(false);
	const [allowTorchButton, setShowTorchButton] = useState(false);
	const [zoomLevel, setZoomLevel] = useState(1);

	const { devices } = useMediaDevices({ constraints: { video: { facingMode: "environment", }, audio: false } });
	const preferredDeviceKeywords = ["back", "rear", "environment"];
	const avoidedDeviceKeywords = ["front", "user", "face", "selfie"];

	const [capabilities, setCapabilities] = useState(null);
	const videoRef = useRef(null);
	const { ref: zxingRef, torch } = useZxing({
		onDecodeResult(result) {
			handleQrScan(result);
		},
		onError(error) {
			handleCameraError(error);
		},
		paused: !showScanDialog,
		deviceId: selectedVideoDeviceId
	});
	if (zxingRef.current) {
		zxingRef.current.onplaying = () => {
			setIsVideoPlaying(true);
		};
	}

	// Helper function to merge multiple refs
	function mergeRefs(...refs) {
		return (videoElement) => {
			for (const ref of refs) {
				if (typeof ref === 'function') {
					ref(videoElement); // Handle callback refs
				} else if (ref && 'current' in ref) {
					ref.current = videoElement; // Handle object refs
				}
			}
		};
	}

	const mergedRef = useCallback(
		(videoElement) => {
			// Pass the video element to both refs
			mergeRefs(videoRef, zxingRef)(videoElement);

			if (videoElement && isVideoPlaying) {
				const mediaStream = videoElement.srcObject;
				if (mediaStream) {
					const [videoTrack] = mediaStream.getVideoTracks();
					const trackCapabilities = videoTrack.getCapabilities();
					if ('zoom' in trackCapabilities) {
						setCapabilities(trackCapabilities);
						videoTrack.applyConstraints({ advanced: [{ zoom: zoomLevel }] });
					}
				}
			}
		},
		[zxingRef, isVideoPlaying]
	);

	const handleZoomChange = (event, newZoomLevel: number) => {
		setZoomLevel(newZoomLevel);
		if (videoRef.current && capabilities) {
			const [videoTrack] = videoRef.current.srcObject.getVideoTracks();
			if (newZoomLevel >= capabilities.zoom.min && newZoomLevel <= capabilities.zoom.max) {
				videoTrack.applyConstraints({ advanced: [{ zoom: newZoomLevel }] });
				setZoomLevel(newZoomLevel);
			}
		}
	};

	const bindPincher = usePinch(
		({ active, movement: [mx, my], offset: [ox, oy], memo }) => {
			// If the gesture is active, update the scale and position
			if (active) {

				// Apply zoom to camera if capabilities are available
				if (videoRef.current && capabilities?.zoom) {
					const newZoomLevel = Math.max(
						capabilities.zoom.min,
						Math.min(capabilities.zoom.max, ox)
					);

					const [videoTrack] = videoRef.current.srcObject.getVideoTracks();
					videoTrack.applyConstraints({ advanced: [{ zoom: newZoomLevel }] });
					setZoomLevel(newZoomLevel);
				}
			}
			return memo; // Return memo for consistent behavior across active states
		},
		{
			scaleBounds: { min: capabilities?.zoom?.min, max: capabilities?.zoom?.max },
			preventDefault: true,
			eventOptions: { passive: false }
		}
	);

	const audio = new Audio(scanBeep);
	const beep = async () => {
		await audio.play().catch((error) => {
			console.error("Error beeping:", error);
		});
	};

	useEffect(() => {
		if (endDate) {
			loadMaterialInventoryData();
		}
	}, [startDate, endDate])

	useEffect(() => {
		switch (ageFilter) {
			case "custom":
				setStartDate(dayjs(new Date(`${new Date().getFullYear()}-${new Date().getMonth() + 1}-01`)).subtract(3, 'month'));
				setEndDate(dayjs());
				break;
			case "-30":
				setStartDate(dayjs().subtract(29, 'day'));
				setEndDate(dayjs());
				break;
			case "30":
			case "60":
			case "90":
			default:
				setStartDate(null);
				setEndDate(dayjs());
				break;
		}
	}, [ageFilter])

	useEffect(() => {
		if (!gridView) return;
		const filteredGridView = gridView.filter((item) => {
			if (idFilter.trim().length > 0 && item.id.toString() != idFilter) {
				return false;
			}
			if (customerFilter && customerFilter != "All" && item.customerId.toString() != customerFilter) {
				return false;
			}
			if (containerFilter && containerFilter != "All" && item.containerId.toString() != containerFilter) {
				return false;
			}
			return true;
		});
		let includedContainerIds: string[] = [];
		let customerIds: string[] = [];
		for (let packet of filteredGridView) {
			if (packet.customerId && !customerIds.includes(packet.customerId.toString())) {
				customerIds.push(packet.customerId.toString());
			}
			if (packet.containerId && !includedContainerIds.includes(packet.containerId.toString())) {
				includedContainerIds.push(packet.containerId.toString());
			}
		}
		setIncludedCustomerIds(customerIds);
		setIncludedContainerIds(includedContainerIds);
	}, [gridView, idFilter, containerFilter, customerFilter]);

	const loadMaterialInventoryData = async () => {
		if (!endDate) return;

		const useStartDate = startDate == null ? "0" : startDate.format("YYYYMMDD");
		const useEndDate = endDate.format("YYYYMMDD");

		setIsLoadingMaterialInventories(true);
		setErrorMessage(undefined);
		setCustomerOptions([]);
		setContainerOptions([]);

		const allowedTilsCustomerIds = userContext.tilsAccess.map(c => c.customerId);

		LoadService.getMaterialInventoriesByDateRange(useStartDate, useEndDate)
			.then((response) => {
				console.log('LoadService.getMaterialInventories: SUCCESS', response);
				const inventories =
					LoadService
						.mapMaterialInventories(response.data).filter((item) => allowedTilsCustomerIds.includes(item.customerId))
						.map((item) => { if (!item.customerLocationId) item.customerLocationName = "None"; return item; });
				let customers: ISelectOption[] = [];
				let containers: ISelectOption[] = [];
				inventories.map((item) => {
					if (item.customerId && !customers.some(c => c.value == item.customerId.toString())) {
						customers.push({ name: item.customerName, value: item.customerId.toString() });
					}

					if (item.containerId && !containers.some(c => c.value == item.containerId.toString())) {
						containers.push({ name: item.containerName, value: item.containerId.toString() });
					}
				});

				setGridView(inventories);
				setCustomerOptions(customers.sort((a, b) => UtilityService.compareStrings(a.name, b.name)));
				setContainerOptions(containers.sort((a, b) => UtilityService.compareStrings(a.name, b.name)));
			})
			.catch((error) => {
				console.log('TrailerService.getTrailersByCustomers: ERROR', error);
				if (error?.code == "ERR_NETWORK") {
					setErrorMessage(AppConfig.apiConnectionErrorMessage)
				}
			})
			.finally(() => {
				setIsLoadingMaterialInventories(false);
			});
	}

	const loadAppSettings = async () => {
		// Low priority, no loading statuses necessary
		const torchSetting = (await AppSettingService.getAppSettingByName("AllowQrScannerTorch"))?.data;
		setShowTorchButton(AppSettingService.mapAppSetting(torchSetting).valueAsBoolean());
	}

	useEffect(() => {
		loadAppSettings();
		loadMaterialInventoryData();
	}, [])

	useEffect(() => {
		if (devices) {
			var filteredDevices = devices.filter(d => d.kind == "videoinput");
			setVideoDevices(filteredDevices);

			if (filteredDevices.length > 0) {
				const preferredDevices = filteredDevices.filter(device => preferredDeviceKeywords.some(keyword => device.label.toLowerCase().includes(keyword)));
				if (preferredDevices.length > 0) {
					setSelectedVideoDeviceId(preferredDevices[0].deviceId);
					return;
				}

				const unavoidedDevices = filteredDevices.filter(device => !avoidedDeviceKeywords.some(keyword => device.label.toLowerCase().includes(keyword)));
				if (unavoidedDevices.length > 0) {
					setSelectedVideoDeviceId(unavoidedDevices[0].deviceId);
					return;
				}

				// The user's device has dumb camera labels and we can't tell which is the rear camera. Just pick the first one
				setSelectedVideoDeviceId(filteredDevices[0].deviceId);
			}
		}
	}, [devices])

	useEffect(() => {
		setIsVideoPlaying(false);
	}, [showScanDialog, selectedVideoDeviceId])

	const handleOpenScanDialog = () => {
		setErrorMessage(undefined);
		setShowScanDialog(true);
	}

	const handleQrScan = async (scan: Result) => {
		await beep();
		setShowScanDialog(false);
		setErrorMessage(undefined);

		let scanJson;
		try {
			const scanText = scan.getText()
				.replaceAll("\x13", "-")  // Replace EN dash with hyphen (QR scanner misreads this as DC3);
				.replaceAll("\x14", "-"); // Replace EM dash with hyphen (QR scanner misreads this as DC4);
			scanJson = JSON.parse(scanText);
		}
		catch (error) {
			setErrorMessage("The scanned QR code does not describe a Material Inventory item.");
			return;
		}

		if (!scanJson.hasOwnProperty("id") || !scanJson.hasOwnProperty("customerLocationId") || !scanJson.hasOwnProperty("remoteShipmentId")) {
			setErrorMessage("The scanned QR code does not describe a Material Inventory item.");
			return;
		}

		const scannedMaterialInventoryId = parseInt(scanJson.id);
		if (Number.isNaN(scannedMaterialInventoryId) || scannedMaterialInventoryId < 1) {
			setErrorMessage("The scanned QR code does not contain a valid Material Inventory ID.");
			return;
		}

		var matchedItem = gridView.find((item) => item.id == scannedMaterialInventoryId);
		if (!matchedItem) {
			setErrorMessage(`The scanned QR code (ID ${scannedMaterialInventoryId}) does not match any Material Inventory items in the list.`);
			return;
		}

		navigate(`/materialinventory/${matchedItem.id}`);
	}

	const handleCameraError = (error: any) => {
		setShowScanDialog(false);
		if (error?.toString()?.toLowerCase()?.includes("permission denied")) {
			setErrorMessage("Your browser has been denied access to the camera. Please allow camera access and try again.");
		}
		else {
			setErrorMessage("An error occurred while trying to access the camera: " + error);
		}
	}

	const handleExportMaterials = () => {
		setErrorMessage(undefined);
		setWorkingModalParams({ params: { content: "Exporting material inventory list..." } });

		const request: IMaterialInventoryExportRequest = {
			id: idFilter.trim().length > 0 ? parseInt(idFilter) : undefined,
			customerId: customerFilter === "All" ? undefined : parseInt(customerFilter),
			containerId: containerFilter === "All" ? undefined : parseInt(containerFilter),
			isLoaded: loadStatusFilter === "All" ? undefined : loadStatusFilter == "Loaded",
			startDate: startDate?.toDate(),
			endDate: endDate.toDate(),
			ageInDays: ageFilter === "All" ? undefined : parseInt(ageFilter)
		};
		LoadService.exportMaterialInvendoryListToExcel(request)
			.then((response) => {
				console.log('LoadService.exportMaterialInvendoryListToExcel: SUCCESS', response);
				downloadFromByteArray(response.data);
			})
			.catch((error) => {
				console.error('LoadService.exportMaterialInvendoryListToExcel: ERROR', error);
				setErrorMessage(ErrorProvider.getError(error));
			})
			.finally(() => {
				setWorkingModalParams(undefined);
			});
	};

	// Keep tabs on the flitered results for the status of the Export to Excel button
	const handleFilteredResultsUpdated = (results: IMaterialInventory[]) => {
		setFilteredResults(results);
	}

	const handleStartDateChange = (value: Dayjs) => {
		setStartDate(value);
		if (startDate && endDate && value.isAfter(endDate)) {
			const age = ageFilter === "All" ? 0 : parseInt(ageFilter);
			setEndDate(value);
		}
	}

	const handleEndDateChange = (value: Dayjs) => {
		setEndDate(value);
		if (startDate && endDate && value.isBefore(startDate)) {
			setStartDate(value);
		}
	}

	const downloadFromByteArray = (file: IGeneratedDocument): void => {
		const uint8ArrayData = new Uint8Array(Buffer.from(file.contents, 'base64'));
		const blob = new Blob([uint8ArrayData], { type: file.contentType });
		const url = window.URL.createObjectURL(blob);

		// Create a hidden anchor element to trigger the download
		const link = document.createElement('a');
		link.href = url;
		link.download = file.name;
		link.style.visibility = 'hidden';
		document.body.appendChild(link);

		// Simulate a click to start the download
		link.click();

		// Clean up the DOM and revoke the object URL (after a slight delay)
		document.body.removeChild(link);
		// A delay is often necessary to ensure the browser has initiated the download
		setTimeout(() => {
			window.URL.revokeObjectURL(url);
		}, 100);
	}

	const isDateRangeSufficientForAgeFiltering = () => {
		if (ageFilter === "All") return true;
		let ageInDays = parseInt(ageFilter);
		ageInDays = ageInDays < 0 ? -ageInDays - 1 : ageInDays + 1; // Add/subtract days when the limit is over/under a number
		if (!startDate || !endDate) return true;
		const diffInDays = endDate.diff(startDate, 'day');
		return diffInDays >= ageInDays;
	}

	return (
		<div className="hwi-material-inventory-list">
			<Navigation></Navigation>
			<LocalizationProvider dateAdapter={AdapterDayjs}>
				<Container maxWidth="lg" className="route-container">
					<PermissionGate permission={userContext.permissions?.canManageMaterialInventory}>
						<section className="hwi-entity-search-bar">
							<div className="search-header">
								<div className="title">Material Inventory</div>
								<div className="controls">
									<div className="date-field">
										{ageFilter === "custom" && startDate &&
											<DatePicker
												className="start-date-field"
												label="Created on or after"
												disableFuture={true}
												value={startDate || dayjs()}
												onChange={(value) => handleStartDateChange(value)}
												slotProps={{
													textField: {
														InputLabelProps: { shrink: true },
													},
												}} />
										}
									</div>
									<div className="date-field">
										{ageFilter === "custom" &&
											<DatePicker
												className="end-date-field"
												disableFuture={true}
												label="Created on or before"
												value={endDate || dayjs()}
												onChange={(value) => handleEndDateChange(value)} />
										}
									</div>
									<div className="button">
										<Button variant="contained" color="primary" disabled={!filteredResults?.length} onClick={() => handleExportMaterials()}>Export Materials</Button>
									</div>
									<div className="button">
										<NavLink to="/materialinventory/add"><Button variant="outlined">New Material</Button></NavLink>
									</div>
								</div>
							</div>

							<section className="search-options">
								<FormControl size="small" className="filter id">
									<TextField
										value={idFilter}
										InputLabelProps={{ shrink: true }}	
										placeholder="Inventory"
										defaultValue="All"
										label="ID"
										onChange={(event: React.ChangeEvent<HTMLInputElement>) => { setIdFilter(event.target.value) }}
									/>
								</FormControl>
								<FormControl size="small" className="filter">
									<InputLabel>Customer</InputLabel>
									<Select
										value={customerFilter}
										defaultValue="All"
										label="Customer"
										onChange={(event: SelectChangeEvent) => { setCustomerFilter(event.target.value) }}
									>
										<MenuItem value="All" key="-1">{customerFilter == 'All' ? 'All' : '(Reset Filter)'}</MenuItem>
										{customerOptions.filter((item) => includedCustomerIds.includes(item.value) || item.value === customerFilter).map((item, index) => (
											<MenuItem value={item.value} key={index}>{item.name}</MenuItem>
										))}
									</Select>
								</FormControl>
								<FormControl size="small" className="filter">
									<InputLabel>Container</InputLabel>
									<Select
										value={containerFilter}
										label="Container"
										onChange={(event: SelectChangeEvent) => { setContainerFilter(event.target.value) }}
									>
										<MenuItem value="All" key="-1">{containerFilter == 'All' ? 'All' : '(Reset Filter)'}</MenuItem>
										{containerOptions.filter((item) => includedContainerIds.includes(item.value.toString()) || item.value.toString() === containerFilter).map((item, index) => (
											<MenuItem value={item.value.toString()} key={index}>{item.name}</MenuItem>
										))}
									</Select>
								</FormControl>
								<FormControl size="small" className="filter load-status">
									<InputLabel>Status</InputLabel>
									<Select
										value={loadStatusFilter}
										defaultValue="All"
										label="Status"
										onChange={(event: SelectChangeEvent) => { setLoadStatusFilter(event.target.value) }}
									>
										<MenuItem value="All" key="-1">All</MenuItem>
										{loadStatusOptions.map((item, index) => (
											<MenuItem value={item.value.toString()} key={index}>{item.name}</MenuItem>
										))}
									</Select>
								</FormControl>
								<FormControl size="small" className="filter age">
									<InputLabel>Age</InputLabel>
									<Select
										value={ageFilter}
										defaultValue="All"
										label="Age"
										onChange={(event: SelectChangeEvent) => { setAgeFilter(event.target.value) }}
									>
										<MenuItem value="custom" key="-1" className="age-option">Custom Range</MenuItem>
										<MenuItem value="-30" key="-30" className="age-option">Under 30 Days</MenuItem>
										<MenuItem value="30" key="30" className="age-option">Over 30 Days</MenuItem>
										<MenuItem value="60" key="60" className="age-option">Over 60 Days</MenuItem>
										<MenuItem value="90" key="90" className="age-option">Over 90 Days</MenuItem>
									</Select>
								</FormControl>
								<FormControl size="small" className="scan">
									<Button variant="contained" color="primary" onClick={() => handleOpenScanDialog()}><Icons.QrCode></Icons.QrCode> Scan</Button>
								</FormControl>
							</section>
						</section>
						<section className="results">
							<Spinner dependency={isLoadingMaterialInventories} text="Loading Inventory...">
								{errorMessage &&
									<Alert className="alert-error"
										severity="error">
										{errorMessage}
									</Alert>
								}
								{gridView &&
									<MaterialInventoryTable
										data={gridView}
										id={idFilter}
										startDate={startDate}
										endDate={endDate}
										customer={customerFilter}
										loadStatus={loadStatusFilter}
										container={containerFilter}
										age={ageFilter}
										onFilteredResultsUpdated={handleFilteredResultsUpdated}
									></MaterialInventoryTable>
								}
							</Spinner>
						</section>
						<Dialog
							fullScreen={false}
							open={showScanDialog}
							className='hwi-material-inventory-qr-scanner-dialog'
							maxWidth='sm'
							keepMounted={true}
							fullWidth={true}>
							<DialogTitle id="title">
								<Spinner dependency={!isVideoPlaying} text='Starting camera...'>
									{"Scan QR Code"}
								</Spinner>
							</DialogTitle>
							{/* The video element can't be in the spinner or it won't be in the DOM and will never start playing */}
							<div className="video-container">
								<video {...bindPincher()} className={isVideoPlaying ? '' : 'hidden'} ref={mergedRef} />
							</div>
							<DialogActions>
								{capabilities?.zoom && (
									<div className="zoom">
										<div className="zoom-level">
											<span className="header">Zoom</span><br />
											<span className="data">
												x<span className="value">{zoomLevel.toFixed(1)}</span>
											</span>
										</div>
										<Slider value={zoomLevel} onChange={handleZoomChange} min={capabilities.zoom.min} max={capabilities.zoom.max} step={capabilities.zoom.step || 0.1} />
									</div>
								)}
								<div className="other">
									{videoDevices?.length > 1 &&
										<Select
											value={selectedVideoDeviceId ?? ""}
											onChange={(event) => setSelectedVideoDeviceId(event.target.value)}
											className='camera-select'
										>
											{videoDevices?.map(device => (
												<MenuItem key={device.deviceId} value={device.deviceId}>
													{device.label}
												</MenuItem>
											))}
										</Select>
									}
									{allowTorchButton && torch?.isAvailable &&
										<Button
											className={"torch" + (torch?.isOn ? " on" : "")}
											onClick={async () => { if (torch?.isOn) { await torch?.off(); } else { await torch?.on(); } }}
										>
											*
										</Button>
									}
									<Button className='cancel' color="error" variant='contained' onClick={() => { setShowScanDialog(false); }}>
										Cancel
									</Button>
								</div>

							</DialogActions>
						</Dialog>
						{/* <Json data={gridView}></Json> */}
					</PermissionGate>
					<WorkingModal params={workingModalParams?.params}></WorkingModal>
				</Container>
			</LocalizationProvider>
		</div>
	);
};

export default MaterialInventoryList;